import React, { useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar } from "./components/Calendar";
import { AvailableTimes } from "./components/AvailableTimes";
import { useBookSlot } from "./hooks/useBookSlot";
import { useBookSlotStore } from "./store";
import { PageLoader } from "@/components/ui/PageLoader";
import { paths } from "@/navigation/paths";
import { useTranslation } from "react-i18next";

export const BookSlot = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    getAvailableTimeSlotsGroup,
    bookTimeSlot,
    refreshAvailableTimeSlotsGroup,
  } = useBookSlot();
  const timeSlotGroup = useBookSlotStore((state) => state.timeSlotGroup);
  const selectedDate = useBookSlotStore((state) => state.selectedDate);
  const selectedTimeSlotId = useBookSlotStore(
    (state) => state.selectedTimeSlotId
  );
  const isLoading = useBookSlotStore((state) => state.isLoading);
  const isBooking = useBookSlotStore((state) => state.isBooking);
  const errorMessage = useBookSlotStore((state) => state.errorMessage);
  const successMessage = useBookSlotStore((state) => state.successMessage);
  const setSelectedDate = useBookSlotStore((state) => state.setSelectedDate);
  const setSelectedTimeSlotId = useBookSlotStore(
    (state) => state.setSelectedTimeSlotId
  );
  const clearSelectedDate = useBookSlotStore(
    (state) => state.clearSelectedDate
  );
  const clearSelectedTimeSlotId = useBookSlotStore(
    (state) => state.clearSelectedTimeSlotId
  );
  const clearMessages = useBookSlotStore((state) => state.clearMessages);

  useEffect(() => {
    if (!id) return;

    // Fetch available time slots for current month
    const currentDate = new Date();
    const month = (currentDate.getMonth() + 1).toString();
    const year = currentDate.getFullYear().toString();

    getAvailableTimeSlotsGroup(id, month, year);
  }, [id]);

  useEffect(() => {
    clearSelectedTimeSlotId();
  }, [selectedDate]);

  useEffect(() => {
    return () => {
      clearSelectedDate();
      clearSelectedTimeSlotId();
    };
  }, []);

  const handleSelectDate = (date: string) => {
    setSelectedDate(date);
    clearMessages();
  };

  const handleSelectTimeSlot = (timeSlotId: number) => {
    setSelectedTimeSlotId(timeSlotId);
  };

  const handleCloseAvailableTimes = () => {
    clearSelectedDate();
  };

  const handleBookSlot = async () => {
    if (!selectedTimeSlotId) return;
    await bookTimeSlot([selectedTimeSlotId]);
  };

  const handleResetCalendar = async () => {
    clearSelectedDate();
    clearSelectedTimeSlotId();
    clearMessages();

    // Refresh available time slots after clearing messages
    if (id) {
      await refreshAvailableTimeSlotsGroup(id);
    }
  };

  const handleGoToBookings = () => {
    navigate(paths.USER_BOOKINGS);
  };

  const handleMonthChange = useCallback(
    (month: number, year: number) => {
      if (!id) return;
      // month is 0-indexed, so add 1 for API call
      const monthStr = (month + 1).toString();
      const yearStr = year.toString();
      getAvailableTimeSlotsGroup(id, monthStr, yearStr);
    },
    [id, getAvailableTimeSlotsGroup]
  );

  // Get available slots for selected date
  const availableSlots =
    selectedDate && timeSlotGroup
      ? timeSlotGroup.availableSlots[selectedDate] || []
      : [];

  return (
    <div className="book-slot-page">
      <PageLoader isLoading={isLoading || isBooking} color="#3b82f6" />

      <div className="book-slot-container">
        {/* Header Section */}
        <div className="header-section">
          <h1 className="page-title">{t("bookSlot.title")}</h1>
          <p className="page-subtitle">{t("bookSlot.subtitle")}</p>
        </div>

        {/* Property Info Card */}
        {timeSlotGroup && (
          <div className="property-info-card">
            <div className="property-header">
              <div className="property-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div>
                <h2 className="property-name">{timeSlotGroup.name}</h2>
                <p className="property-description">
                  {t("bookSlot.propertyDescription")}
                </p>
              </div>
            </div>

            {/* Legend */}
            <div className="legend">
              <div className="legend-item">
                <div className="legend-dot available"></div>
                <span>{t("bookSlot.legend.available")}</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot today"></div>
                <span>{t("bookSlot.legend.today")}</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot past"></div>
                <span>{t("bookSlot.legend.past")}</span>
              </div>
            </div>
          </div>
        )}

        {/* Calendar and Available Times Section */}
        {timeSlotGroup && (
          <div className="content-grid">
            <div className="calendar-section">
              <Calendar
                timeSlotGroup={timeSlotGroup}
                onBookSlot={handleSelectDate}
                selectedDate={selectedDate}
                disabled={!!errorMessage || !!successMessage}
                onMonthChange={handleMonthChange}
              />
            </div>

            {/* Available Times Sidebar or Status Messages */}
            {errorMessage && (
              <div className="available-times-section">
                <div className="error-banner sidebar-banner">
                  <svg
                    className="banner-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="banner-content">
                    <h3 className="banner-title">{t("bookSlot.errorTitle")}</h3>
                    <p className="banner-message">{errorMessage}</p>
                  </div>
                  <button
                    className="banner-button error-button"
                    onClick={handleResetCalendar}
                  >
                    {t("bookSlot.errorBack")}
                  </button>
                </div>
              </div>
            )}

            {successMessage && !errorMessage && (
              <div className="available-times-section">
                <div className="success-banner sidebar-banner">
                  <svg
                    className="banner-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="banner-content">
                    <h3 className="banner-title">{t("bookSlot.successTitle")}</h3>
                    <p className="banner-message">{successMessage}</p>
                  </div>
                  <div className="banner-actions">
                    <button
                      className="banner-button secondary-button"
                      onClick={handleResetCalendar}
                    >
                      {t("bookSlot.successBookAnother")}
                    </button>
                    <button
                      className="banner-button primary-button"
                      onClick={handleGoToBookings}
                    >
                      {t("bookSlot.successViewBookings")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {selectedDate && !errorMessage && !successMessage && (
              <div className="available-times-section">
                <AvailableTimes
                  selectedDate={selectedDate}
                  timeSlots={availableSlots}
                  selectedTimeSlotId={selectedTimeSlotId}
                  onSelectTimeSlot={handleSelectTimeSlot}
                  onBook={handleBookSlot}
                  onClose={handleCloseAvailableTimes}
                  isBooking={isBooking}
                />
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!timeSlotGroup && !isLoading && !errorMessage && (
          <div className="empty-state">
            <svg className="empty-icon" fill="none" viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="empty-title">{t("bookSlot.emptyTitle")}</h3>
            <p className="empty-description">{t("bookSlot.emptyDescription")}</p>
          </div>
        )}
      </div>

      <style>{`
        .book-slot-page {
          min-height: 100vh;
          background: #e0f2fe;
          padding: 2rem 1rem;
          position: relative;
        }

        .book-slot-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .header-section {
          text-align: center;
          margin-bottom: 3rem;
          color: #1e293b;
        }

        .page-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin: 0 0 0.5rem 0;
          color: #0f172a;
        }

        .page-subtitle {
          font-size: 1.125rem;
          color: #475569;
          margin: 0;
        }

        .sidebar-banner {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          animation: slideInRight 0.3s ease-out;
          height: fit-content;
        }

        .sidebar-banner.error-banner {
          border: 2px solid #ef4444;
        }

        .sidebar-banner.success-banner {
          border: 2px solid #10b981;
        }

        .sidebar-banner .banner-icon {
          width: 48px;
          height: 48px;
          align-self: center;
        }

        .sidebar-banner.error-banner .banner-icon {
          color: #dc2626;
        }

        .sidebar-banner.success-banner .banner-icon {
          color: #059669;
        }

        .sidebar-banner .banner-content {
          text-align: center;
        }

        .sidebar-banner .banner-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 0.5rem 0;
        }

        .sidebar-banner.error-banner .banner-title {
          color: #991b1b;
        }

        .sidebar-banner.success-banner .banner-title {
          color: #065f46;
        }

        .sidebar-banner .banner-message {
          font-size: 1rem;
          margin: 0;
          line-height: 1.6;
        }

        .sidebar-banner.error-banner .banner-message {
          color: #dc2626;
        }

        .sidebar-banner.success-banner .banner-message {
          color: #047857;
        }

        .sidebar-banner .banner-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .sidebar-banner .banner-button {
          padding: 0.875rem 1.5rem;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          width: 100%;
        }

        .sidebar-banner .error-button {
          background: #dc2626;
          color: white;
        }

        .sidebar-banner .error-button:hover {
          background: #b91c1c;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
        }

        .sidebar-banner .primary-button {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        .sidebar-banner .primary-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .sidebar-banner .secondary-button {
          background: white;
          color: #059669;
          border: 2px solid #10b981;
        }

        .sidebar-banner .secondary-button:hover {
          background: #ecfdf5;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .property-info-card {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
          animation: slideIn 0.4s ease-out;
        }

        .property-header {
          display: flex;
          align-items: start;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .property-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .property-icon svg {
          width: 32px;
          height: 32px;
          color: white;
        }

        .property-name {
          font-size: 1.75rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 0.5rem 0;
        }

        .property-description {
          font-size: 1rem;
          color: #666;
          margin: 0;
        }

        .legend {
          display: flex;
          gap: 2rem;
          flex-wrap: wrap;
          padding-top: 1.5rem;
          border-top: 1px solid #e5e7eb;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #666;
        }

        .legend-dot {
          width: 20px;
          height: 20px;
          border-radius: 6px;
          border: 2px solid;
        }

        .legend-dot.available {
          background: #eff6ff;
          border-color: #3b82f6;
        }

        .legend-dot.today {
          background: #fef3c7;
          border-color: #fbbf24;
        }

        .legend-dot.past {
          background: #f9f9f9;
          border-color: #e5e7eb;
          opacity: 0.5;
        }

        .content-grid {
          display: flex;
          gap: 3rem;
          animation: slideIn 0.5s ease-out;
          align-items: flex-start;
        }

        .calendar-section {
          flex: 1;
          min-width: 0;
          max-width: 460px;
        }

        .available-times-section {
          flex: 0 0 90%;
          max-width: 500px;
          min-width: 0;
        }

        @media (min-width: 1024px) {
          .content-grid {
            flex-direction: row;
          }
          
          .available-times-section {
            flex: 0 0 90%;
          }
        }

        .empty-state {
          background: white;
          border-radius: 16px;
          padding: 4rem 2rem;
          text-align: center;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
          animation: slideIn 0.4s ease-out;
        }

        .empty-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 1.5rem;
          color: #9ca3af;
        }

        .empty-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 0.5rem 0;
        }

        .empty-description {
          font-size: 1rem;
          color: #6b7280;
          margin: 0;
          max-width: 400px;
          margin: 0 auto;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 1023px) {
          .content-grid {
            flex-direction: column;
          }

          .calendar-section {
            max-width: 100%;
          }

          .available-times-section {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 50;
            display: flex;
            align-items: flex-end;
            animation: fadeIn 0.3s ease-out;
            flex: none;
            max-width: none;
          }

          .available-times-section > div {
            max-height: 80vh;
            border-radius: 16px 16px 0 0;
            width: 100%;
          }

          .sidebar-banner {
            max-height: none;
            margin: 1rem;
            border-radius: 16px;
          }
        }

        @media (max-width: 768px) {
          .book-slot-page {
            padding: 1.5rem 1rem;
          }

          .page-title {
            font-size: 2rem;
          }

          .page-subtitle {
            font-size: 1rem;
          }

          .property-info-card {
            padding: 1.5rem;
          }

          .property-header {
            flex-direction: column;
            gap: 1rem;
          }

          .property-name {
            font-size: 1.5rem;
          }

          .legend {
            gap: 1rem;
          }

          .empty-state {
            padding: 3rem 1.5rem;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};
